import {
    Badge,
    Button,
    CardList,
    Flexer,
    Form,
    FormControl,
    FormHelperText,
    FormLabel,
    getKey,
    Heading,
    Icon,
    If,
    Input,
    InputControl,
    InputPrefix,
    Link,
    Menu,
    MenuItem,
    MenuItemOption,
    MenuOptionGroup,
    Modal,
    ModalClose,
    Notification,
    NotificationContent,
    Pagination,
    Pill,
    plural,
    Popover,
    Portal,
    Stack,
    stopEvent,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    TagInput,
    TagInputField,
    Text,
    useAlert,
    useDialog,
    useInput,
    UserList,
    UserListUser,
    useToast,
    useVisibility,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import {
    TbCircleMinus,
    TbDots,
    TbMinus,
    TbSearch,
    TbSend,
    TbSquareRoundedMinus,
    TbTrash,
    TbUser,
    TbUserPlus,
} from 'react-icons/tb'
import { useParams } from 'react-router'
import { Error, Loading } from '../components'
import { TeamUserPreview } from '../components/common/team-user-preview'
import { byEmail, byName, generateRandomNextPosition, logger, validEmail } from '../helpers/util'
import { useTeams, useWorkspaceInvites, useWorkspaceUsers } from '../hooks/api.hooks'
import { useAppStore } from '../store/app'
import NewTeamModal from './new-team.modal'
import { TeamModal } from './team.modal'

const InviteList = ({ selected, onSelect }) => {
    const [text, setText] = useState<any>('')

    const handleBlur = (e) => {
        e.preventDefault()
        e.stopPropagation()

        if (validEmail(text)) {
            onSelect([...selected, text])
            setText('')
        }
    }

    const handleKeyDown = (e) => {
        const { isEnter, isBackspace } = getKey(e)

        if (isEnter) {
            e.preventDefault()
            e.stopPropagation()
            if (validEmail(text)) {
                onSelect([...selected, text])
                setText('')
            }
        }
        if (isBackspace && !e.target.value) onSelect(selected.filter((s, i) => i != selected.length - 1))
    }

    return (
        <TagInput
            render={() => {
                return selected.map((name, index) => (
                    <Pill
                        key={index}
                        outline>
                        {name}
                    </Pill>
                ))
            }}>
            <TagInputField
                value={text}
                placeholder="Add email..."
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
            />
        </TagInput>
    )
}

const UserRoleMenu = ({ user, onRoleChange }) => {
    const { visible, show, hide } = useVisibility(false)

    const handleRoleChange = (role) => {
        onRoleChange({ user, role })
        hide()
    }

    return (
        <Popover
            arrow
            portal={Portal}
            focusTrap
            width={200}
            content={
                <Menu
                    border="none"
                    width={200}>
                    <MenuOptionGroup
                        type="radio"
                        title={user.name}
                        onChange={handleRoleChange}
                        defaultValue={user.role}>
                        <MenuItemOption value="Owner">Owner</MenuItemOption>
                        <MenuItemOption value="Admin">Admin</MenuItemOption>
                        <MenuItemOption value="Member">Member</MenuItemOption>
                        <MenuItemOption value="Guest">Guest</MenuItemOption>
                    </MenuOptionGroup>
                </Menu>
            }
            isVisible={visible}
            onDismiss={hide}>
            <Link
                size="sm"
                onClick={show}
                className="f-underline">
                {user.role}&nbsp;&nbsp;▾
            </Link>
        </Popover>
    )
}

const UserMenu = ({ user, onRemove }) => {
    const { visible, show, hide } = useVisibility(false)

    const handleRemove = () => {
        hide()
        onRemove(user.workspaceUserId)
    }

    return (
        <Popover
            arrow
            portal={Portal}
            focusTrap
            width={200}
            content={
                <Menu
                    border="none"
                    width={200}>
                    <MenuItem
                        onClick={handleRemove}
                        prefix={<Icon icon={TbTrash} />}>
                        Remove User
                    </MenuItem>
                </Menu>
            }
            isVisible={visible}
            onDismiss={hide}>
            <Button
                size="sm"
                onClick={show}
                subtle>
                <Icon icon={TbDots} />
            </Button>
        </Popover>
    )
}

const UsersList = ({ users, filter, onRoleChange, onRemove }) => {
    const pageSize = 10
    const [pageUsers, setPageUsers] = useState(1)
    const workspace = useAppStore((state) => state.workspace)
    const isAdmin = useMemo(() => workspace.role == 'Owner' || workspace.role == 'Admin', [workspace.role])
    const filteredUsers = useMemo(() => {
        return users.filter((user: any) => user.name.toLowerCase().includes(filter.toLowerCase()))
    }, [users, filter])
    const subscriptionText = useMemo(() => {
        if (workspace.subscriptionPlan == 'team') {
            return 30 + ' users'
        } else {
            return workspace.subscriptionQty == 1 ? '1 user' : workspace.subscriptionQty + ' users'
        }
    }, [workspace.subscriptionQty, workspace.subscriptionPlan])

    return (
        <>
            {!!filteredUsers.length && (
                <>
                    <View
                        row
                        m="0 0 1rem 0"
                        justifyContent="flex-end"
                        width="100%">
                        <Text m="0 auto 0 0">
                            {filteredUsers.length} {plural(filteredUsers.length, 'user')}
                        </Text>
                        <Text colorToken="accent">Your plan allows {subscriptionText}</Text>
                    </View>
                    <UserList
                        style={{
                            '--f-size-10': '2rem',
                        }}
                        shadow="none"
                        users={filteredUsers
                            .filter(
                                (_, index: number) =>
                                    index >= pageSize * (pageUsers - 1) && index < pageSize * (pageUsers - 1) + pageSize
                            )
                            .map(
                                (user: any): UserListUser => ({
                                    id: user.id,
                                    name: user.name,
                                    description: user.title,
                                    image: user.image,
                                    tools: (
                                        <If if={isAdmin}>
                                            <View
                                                row
                                                gap="2rem">
                                                <UserRoleMenu
                                                    user={user}
                                                    onRoleChange={onRoleChange}
                                                />
                                                <UserMenu
                                                    user={user}
                                                    onRemove={onRemove}
                                                />
                                            </View>
                                        </If>
                                    ),
                                })
                            )}
                    />
                </>
            )}

            {!filteredUsers.length && <Text p="2rem 0">No users found</Text>}

            {!!filteredUsers.length && (
                <View
                    row
                    width="100%"
                    m="0 auto"
                    p="1rem 0 0 0">
                    <Pagination
                        width={150}
                        pageSize={10}
                        totalCount={filteredUsers.length}
                        currentPage={pageUsers}
                        siblingCount={4}
                        separator="..."
                        onPageChange={setPageUsers}
                    />
                </View>
            )}
        </>
    )
}

const InvitesList = ({ filter, invites, onResendInvite, onRemoveInvite }) => {
    const pageSize = 10
    const [pageInvites, setPageInvites] = useState(1)
    const filteredInvites = useMemo(() => {
        return invites.filter((invite: any) => invite.email.toLowerCase().includes(filter.toLowerCase()))
    }, [invites, filter])

    return (
        <>
            {!!filteredInvites.length && (
                <>
                    <View
                        row
                        m="0 0 1rem 0"
                        justifyContent="flex-end"
                        width="100%">
                        <Text m="0 auto 0 0">
                            {filteredInvites.length} {plural(filteredInvites.length, 'invite')}
                        </Text>
                    </View>

                    <UserList
                        style={{
                            '--f-size-10': '2rem',
                        }}
                        shadow="none"
                        users={filteredInvites
                            .filter(
                                (_, index: number) =>
                                    index >= pageSize * (pageInvites - 1) &&
                                    index < pageSize * (pageInvites - 1) + pageSize
                            )
                            .map(
                                (invite: any): UserListUser => ({
                                    id: invite.id,
                                    name: invite.email,
                                    description: '',
                                    image: '',
                                    tools: (
                                        <View
                                            row
                                            gap="2rem">
                                            <Link
                                                size="sm"
                                                onClick={() => onResendInvite(invite.id)}
                                                className="f-underline">
                                                Resend Invite
                                            </Link>
                                            <Button
                                                size="sm"
                                                onClick={() => onRemoveInvite(invite.id)}
                                                subtle>
                                                <Icon icon={TbCircleMinus} />
                                            </Button>
                                        </View>
                                    ),
                                })
                            )}
                    />
                </>
            )}

            {!filteredInvites.length && <Text p="2rem 0">No invites found</Text>}

            {!!filteredInvites.length && (
                <View
                    row
                    width="100%"
                    m="0 auto"
                    p="1rem 0 0 0">
                    <Pagination
                        width={150}
                        pageSize={10}
                        totalCount={filteredInvites.length}
                        currentPage={pageInvites}
                        siblingCount={4}
                        separator="..."
                        onPageChange={(page) => setPageInvites(page)}
                    />
                </View>
            )}
        </>
    )
}

const TeamList = ({ teams, onView }) => {
    const { workspaceId } = useParams()

    return (
        <>
            {!!teams.length && (
                <>
                    <View
                        row
                        m="0 0 1rem 0"
                        justifyContent="flex-end"
                        width="100%">
                        <Text m="0 auto 0 0">
                            {teams.length} {plural(teams.length, 'team')}
                        </Text>
                    </View>

                    <CardList
                        shadow="none"
                        width="100%">
                        {teams.map((team: any, index) => (
                            <View
                                row
                                key={index}
                                justifyContent="flex-start"
                                p={20}>
                                <View
                                    column
                                    flex={1}
                                    gap="0.5rem"
                                    alignItems="flex-start">
                                    <Heading
                                        as="h4"
                                        color={team.color}>
                                        {team.name}
                                    </Heading>
                                    <View
                                        row
                                        gap="1rem">
                                        <Text
                                            size="sm"
                                            colorToken="text-weak">
                                            {team.total_projects} {plural(team.total_projects, 'project')}
                                        </Text>
                                        <Text
                                            size="sm"
                                            colorToken="text-weak">
                                            {team.total_team_users} {plural(team.total_team_users, 'member')}
                                        </Text>
                                        <Link
                                            size="sm"
                                            onClick={() => onView(team.id)}
                                            className="f-underline">
                                            Manage
                                        </Link>
                                    </View>
                                </View>

                                <TeamUserPreview
                                    workspaceId={workspaceId}
                                    teamId={team.id}
                                    size="lg"
                                />
                            </View>
                        ))}
                    </CardList>
                </>
            )}

            {!teams.length && <Text p="2rem 0">No teams found</Text>}
        </>
    )
}

export const WorkspaceMembersModal = ({ onDismiss }) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const { workspaceId } = useParams()
    const inviteDialog = useVisibility()
    const teamName = useInput('')
    const newTeamModal = useVisibility(false)
    const teamModal = useVisibility(false)
    const [option, setOption] = useState(0)
    const [filter, setFilter] = useState('')
    const [users, setUsers] = useState<any>([])
    const [invites, setInvites] = useState<any>([])
    const { setDialog, closeDialog } = useDialog()
    const workspaceInvites = useWorkspaceInvites()
    const workspaceUsers = useWorkspaceUsers()
    const teamsAPI = useTeams()
    const { alert } = useAlert()
    const { showToast } = useToast()
    const [emails, setEmails] = useState<any>([])
    const [teams, setTeams] = useState([])
    const [teamId, setTeamId] = useState(null)
    const actions = useAppStore((state) => state.actions)
    const workspace = useAppStore((state) => state.workspace)
    const isAdmin = useMemo(() => workspace.role == 'Owner' || workspace.role == 'Admin', [workspace.role])
    const limitReached = useMemo(() => {
        if (workspace.subscriptionPlan == 'team') {
            return 30 <= users.length
        } else {
            return +workspace.subscriptionQty <= users.length
        }
    }, [workspace.subscriptionQty, workspace.subscriptionPlan, users])

    const handleRemoveInvite = async (inviteId) => {
        setDialog({
            title: 'Are you sure?',
            description: 'Are you sure you want to remove this invite?',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            workspaceInvites
                                .remove(workspaceId, inviteId)
                                .then(() => {
                                    showToast({ message: 'The invite has been removed.', anchor: 'bottom-right' })
                                    fetchDataInvites()
                                    setLoading(false)
                                    closeDialog()
                                })
                                .catch((e) => {
                                    showToast({ message: 'The invite has been removed.', anchor: 'bottom-right' })
                                    fetchDataInvites()
                                    setLoading(false)
                                    setError(typeof e == 'string' ? e : 'An error occured')
                                    closeDialog()
                                })
                        }}
                        variant="danger">
                        Remove
                    </Button>
                </View>
            ),
        })
    }

    const handleResendInvite = async (inviteId) => {
        setLoading(true)
        setError(null)
        try {
            await workspaceInvites.resend(workspaceId, inviteId)
            showToast({ message: 'The invite has been resent.', anchor: 'bottom-right' })
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleRemoveUser = async (workspaceUserId) => {
        setDialog({
            title: 'Are you sure?',
            description: 'They will lose access to this workspace and all the resources.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            workspaceUsers
                                .remove(workspaceId, workspaceUserId)
                                .then(() => {
                                    fetchDataUsers()
                                    setLoading(false)
                                    closeDialog()
                                })
                                .catch(() => {
                                    fetchDataUsers()
                                    setLoading(false)
                                    setError('Error')
                                    closeDialog()
                                })
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    const handleRoleChange = async ({ user, role }) => {
        setLoading(true)
        setError(null)

        try {
            const { workspaceUserId } = user
            await workspaceUsers.update(workspaceId, workspaceUserId, { role })

            fetchDataUsers()
            setLoading(false)
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const fetchDataUsers = async () => {
        setLoading(true)
        setError(null)
        try {
            const users = await workspaceUsers.getAll(workspaceId)
            setUsers(users.sort(byName))
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const fetchDataInvites = async () => {
        try {
            const invites = await workspaceInvites.getAll(workspaceId)
            setInvites(invites.sort(byEmail))
        } catch (e) {
            logger(e)
        }
    }

    const fetchDataTeams = async () => {
        try {
            const teams = await teamsAPI.getAll(workspaceId)
            setTeams(teams.sort(byName))
        } catch (e) {
            logger(e)
        }
    }

    const handleSendInvites = async (e) => {
        stopEvent(e)
        try {
            await workspaceInvites.create(workspaceId, { emails })
            showToast({ message: 'An invitation email has been sent to these mailboxes!', anchor: 'bottom-right' })
            inviteDialog.hide()
            fetchDataInvites()
            setEmails([])
        } catch (e) {
            logger(e)
        }
    }

    const handleTeamCreate = async () => {
        setLoading(true)
        setError(false)

        try {
            const team = await teamsAPI.create(workspaceId, {
                name: teamName.value,
                position: generateRandomNextPosition(teams),
            })
            fetchDataTeams()
            teamName.setValue('')
            actions.teamAdd(team)
            showToast({ message: 'Team successfully created.', anchor: 'bottom-right' })
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    useEffect(() => {
        fetchDataUsers()
        fetchDataInvites()
        fetchDataTeams()
    }, [workspaceId])

    return (
        <>
            {!!teamId && (
                <TeamModal
                    teamId={teamId}
                    onDismiss={() => setTeamId(null)}
                    onDelete={() => fetchDataTeams()}
                    onUpdate={() => fetchDataTeams()}
                />
            )}

            {newTeamModal.visible && (
                <NewTeamModal
                    onDismiss={newTeamModal.hide}
                    onCreate={() => null}
                />
            )}

            <Modal
                disableBackgroundDismiss
                width={800}
                height="98%"
                anchor="middle-center"
                onDismiss={onDismiss}
                isVisible={true}
                style={{ '--f-modal-body-padding': 0, 'maxHeight': 900 }}
                header={
                    <>
                        <Heading as="h4">People & Teams</Heading>
                        <Flexer />
                        <ModalClose onClick={onDismiss} />
                    </>
                }
                footer={
                    <Stack
                        p={10}
                        spacing={10}
                        width="100%">
                        <Button onClick={onDismiss}>Close</Button>
                        <Flexer />
                        <Button
                            disabled={!isAdmin || limitReached}
                            outline
                            variant="accent"
                            onClick={inviteDialog.show}
                            prefix={<Icon icon={TbUserPlus} />}>
                            Invite People
                        </Button>
                        <Button
                            variant="accent"
                            onClick={onDismiss}>
                            Okay
                        </Button>
                    </Stack>
                }>
                <Error error={error} />
                <Loading loading={loading} />
                <Tabs
                    selected={option}
                    onSelect={setOption}
                    layout="left">
                    <TabList width={200}>
                        <Tab suffix={<Badge size="sm">{String(users.length)}</Badge>}>Members</Tab>
                        <Tab
                            suffix={<Badge size="sm">{String(invites.length)}</Badge>}
                            disabled={!isAdmin}>
                            Invites
                        </Tab>
                        <Tab
                            suffix={<Badge size="sm">{String(teams.length)}</Badge>}
                            disabled={!isAdmin}>
                            Teams
                        </Tab>
                    </TabList>

                    <TabPanels>
                        <TabPanel>
                            <View
                                zIndex={0}
                                width="100%"
                                height="100%"
                                position="absolute"
                                style={{ inset: 0 }}
                                p="1rem"
                                className="f-overflow-y-auto">
                                {limitReached && (
                                    <Notification
                                        radius="var(--f-radius)"
                                        variant="danger"
                                        m="0 0 1rem 0">
                                        <NotificationContent>
                                            You have reached the maximum amount of people allowed.
                                        </NotificationContent>
                                    </Notification>
                                )}
                                <InputControl m="0 0 1rem 0">
                                    <InputPrefix>
                                        <Icon icon={TbSearch} />
                                    </InputPrefix>
                                    <Input
                                        flex={2}
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                        placeholder="Type to filter..."
                                    />
                                </InputControl>
                                <UsersList
                                    filter={filter}
                                    users={users}
                                    onRoleChange={handleRoleChange}
                                    onRemove={handleRemoveUser}
                                />
                            </View>
                        </TabPanel>
                        <TabPanel>
                            <View
                                zIndex={0}
                                width="100%"
                                height="100%"
                                position="absolute"
                                style={{ inset: 0 }}
                                p="1rem"
                                className="f-overflow-y-auto">
                                <InputControl m="0 0 1rem 0">
                                    <InputPrefix>
                                        <Icon icon={TbSearch} />
                                    </InputPrefix>
                                    <Input
                                        flex={2}
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                        placeholder="Type to filter..."
                                    />
                                </InputControl>
                                <InvitesList
                                    filter={filter}
                                    invites={invites}
                                    onResendInvite={handleResendInvite}
                                    onRemoveInvite={handleRemoveInvite}
                                />
                            </View>
                        </TabPanel>
                        <TabPanel>
                            <View
                                zIndex={0}
                                width="100%"
                                height="100%"
                                position="absolute"
                                style={{ inset: 0 }}
                                p="1rem"
                                className="f-overflow-y-auto">
                                <View
                                    row
                                    gap="0.5rem"
                                    m="0 0 1rem 0">
                                    <InputControl>
                                        <InputPrefix>
                                            <Icon icon={TbUser} />
                                        </InputPrefix>
                                        <Input
                                            flex={2}
                                            value={teamName.value}
                                            onChange={teamName.onChange}
                                            placeholder="Engineering"
                                        />
                                    </InputControl>
                                    <Button
                                        onClick={handleTeamCreate}
                                        variant="accent"
                                        disabled={!teamName.value}>
                                        Create Team
                                    </Button>
                                </View>
                                <TeamList
                                    teams={teams}
                                    onView={setTeamId}
                                />
                            </View>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Modal>

            <Modal
                width={500}
                height="fit-content"
                onDismiss={inviteDialog.hide}
                isVisible={inviteDialog.visible}
                footer={
                    <>
                        <Button
                            onClick={() => {
                                inviteDialog.hide()
                                setEmails([])
                            }}>
                            Close
                        </Button>
                        <Flexer />
                        <Button
                            type="submit"
                            form="workspace-invites"
                            variant="accent"
                            onClick={handleSendInvites}
                            prefix={<Icon icon={TbSend} />}
                            disabled={loading || !emails.length}>
                            Send Invites
                        </Button>
                    </>
                }>
                <Heading as="h3">Invite People</Heading>
                <Form>
                    <FormControl>
                        <FormLabel htmlFor="emails">Add an email address and press enter</FormLabel>
                        <InviteList
                            selected={emails}
                            onSelect={setEmails}
                        />
                        <FormHelperText kind="info">
                            You are able to invite a maximum of 10 people per time.
                        </FormHelperText>
                    </FormControl>
                </Form>
            </Modal>
        </>
    )
}
